import { useModal } from '@ebay/nice-modal-react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'

import {
  CHROME_BAR_HEIGHT,
  SPORTS_KEEP_ALIVE_MS,
  SPORTS_PATH,
  SPORTS_SESSION_RELOAD_COOLDOWN_MS,
  SPORTS_SHOW_BOTTOM_NAV,
  SPORTS_SHOW_HEADER,
} from '../../constants/sports'
import useDimensions from '../../hooks/useDimensions'
import { isLobbyOAuthProvider, useOAuthLogin } from '../../hooks/useOAuthLogin'
import { usePasswordLogin } from '../../hooks/usePasswordLogin'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { useBoundStore } from '../../store/store'
import type { Theme } from '../../types/common'
import { api } from '../../utils/api-client'
import { SPORTS_LOBBY_GAME_ID } from '../../utils/game-url'
import { trackEvent } from '../../utils/gtm'
import normalizePathname from '../../utils/normalizePathname'
import {
  fetchSportsLauncherUrl,
  getSportsUrlCacheKey,
  readCachedSportsUrl,
} from '../../utils/sports-launcher'
import { ErrorPage, GameLoading, ToastMessage } from '../common'
import DepositModal from '../wallet-management/deposit-modal'

// Tolerates a JSON string or a plain object.
const parseLobbyMessage = (
  raw: unknown,
): Record<string, unknown> | null => {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
}

const isTheme = (value: unknown): value is Theme =>
  value === 'dark' || value === 'light'

// In-site paths only; a full URL or `//host` would be an open redirect.
const isInSitePath = (value: unknown): value is string =>
  typeof value === 'string' && /^\/(?![/\\])/.test(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

interface SportsLobbyProps {
  // False while parked: still running, just not on screen.
  active: boolean
  eventId?: string
}

const SportsLobby = ({ active, eventId }: SportsLobbyProps) => {
  const { refreshBalance } = useRefreshBalance()
  const { isDesktop } = useDimensions()
  const depositModal = useModal(DepositModal)
  const navigate = useNavigate()
  const username = useBoundStore(state => state.user.username)
  const isUserLoading = useBoundStore(state => state.user.isLoading)
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const setSportsTheme = useBoundStore(state => state.common.setSportsTheme)
  const [isLoading, setIsLoading] = useState(true)
  const [gameUrl, setGameUrl] = useState('')
  const gameUrlRef = useRef('')
  const [apiError, setApiError] = useState<string | null>(null)
  // Bumped when a delegated login ends, to force one fresh iframe load.
  const [reloadNonce, setReloadNonce] = useState(0)
  const prevReloadNonce = useRef(0)

  const toastMessage = useModal(ToastMessage)
  // The iframe can't open the OAuth popup, so it delegates it to the parent.
  const { startOAuthLogin } = useOAuthLogin({
    onSettled: () => setReloadNonce(n => n + 1),
    // OAuth can't run here (disabled/popup blocked): degrade to the login page.
    onCannotStart: () => {
      void navigate({ to: '/register' })
    },
    onError: text => {
      void toastMessage.show({ text, isValid: false })
    },
  })
  // Delegated username/password login; success always forces a fresh ticket.
  const { startPasswordLogin } = usePasswordLogin({
    onSuccess: () => setReloadNonce(n => n + 1),
    onError: text => {
      void toastMessage.show({ text, isValid: false })
    },
    onRedirect: path => {
      if (isInSitePath(path)) navigate({ to: path })
    },
  })

  // The message listener binds once, so reach the latest value through a ref.
  const startOAuthLoginRef = useRef(startOAuthLogin)
  startOAuthLoginRef.current = startOAuthLogin
  const startPasswordLoginRef = useRef(startPasswordLogin)
  startPasswordLoginRef.current = startPasswordLogin
  const usernameRef = useRef(username)
  usernameRef.current = username
  const lastSessionReloadRef = useRef(0)

  useEffect(() => {
    if (!active) return
    trackEvent('open_game', {
      event_category: 'sports',
      event_action: 'open_sports_bti',
      event_label: 'Bti Sports',
    })
  }, [active])

  // Re-sent on every entry: the backend reads it as "player is in sports now".
  useEffect(() => {
    if (!active || !username) return
    api.post('/conduct.php', {
      type: 'change_casino_or_sports',
      plyer_category: 1,
    })
  }, [active, username])

  // Read in the handler so the listener binds once, before the iframe exists.
  const lobbyOriginRef = useRef('')
  useEffect(() => {
    try {
      lobbyOriginRef.current = gameUrl ? new URL(gameUrl).origin : ''
    } catch {
      lobbyOriginRef.current = ''
    }
  }, [gameUrl])

  // Bound on mount so no message is missed once the iframe starts loading.
  useEffect(() => {
    const messageHandler = (message: MessageEvent) => {
      // Only the lobby we launched may drive the parent page.
      if (!lobbyOriginRef.current || message.origin !== lobbyOriginRef.current)
        return

      const data = parseLobbyMessage(message.data)
      switch (data?.type) {
        case 'loginNeed':
          navigate({ to: '/register' })
          break
        case 'depositNeed':
          depositModal.show()
          break
        // Delegated third-party login; see docs/funsports-iframe-postmessage.md §3.
        case 'oauthLogin':
          if (isLobbyOAuthProvider(data.provider))
            void startOAuthLoginRef.current(data.provider)
          break
        // Delegated username/password login; see docs/funsports-iframe-postmessage.md §3bis.
        case 'passwordLogin':
          if (isNonEmptyString(data.username) && isNonEmptyString(data.password))
            void startPasswordLoginRef.current(data.username, data.password)
          break
        // The lobby lost its own session, typically across one of its deploys.
        case 'sessionExpired': {
          if (!usernameRef.current) {
            navigate({ to: '/register' })
            break
          }
          const now = Date.now()
          const since = now - lastSessionReloadRef.current
          // A lobby still broken after the reload asks again right away.
          if (since < SPORTS_SESSION_RELOAD_COOLDOWN_MS) break
          lastSessionReloadRef.current = now
          setReloadNonce(n => n + 1)
          break
        }
        // The lobby cannot jump to fun88 itself: this app bails out of any frame.
        case 'navigate':
          if (isInSitePath(data.path)) navigate({ to: data.path })
          break
        // Only our own overlays are themed here; the lobby paints its own.
        case 'theme':
          if (isTheme(data.theme)) {
            localStorage.setItem('sportsTheme', data.theme)
            setSportsTheme(data.theme)
          }
          break
        default:
          break
      }
    }

    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [])

  // Balance may have moved in the casino while the lobby sat parked.
  useEffect(() => {
    if (!active || isUserLoading || !username) return
    api.post('/ajax_data.php', {
      type: 'auto_transfer_in',
      gameId: SPORTS_LOBBY_GAME_ID,
    })
    refreshBalance()
  }, [active, isUserLoading, username])

  // This component outlives the route, so a login mid-life must refetch.
  useEffect(() => {
    if (isUserLoading) return
    // A post-login reload must skip the cache so an unchanged URL reloads too.
    const forceReload = prevReloadNonce.current !== reloadNonce
    prevReloadNonce.current = reloadNonce
    let cancelled = false

    const showUrl = (url: string) => {
      if (cancelled) return
      if (gameUrlRef.current === url) {
        // Same document already on screen — no reload, so no onLoad to wait for.
        setIsLoading(false)
        return
      }
      gameUrlRef.current = url
      setIsLoading(true)
      setGameUrl(url)
    }

    const cacheKey = getSportsUrlCacheKey(username, isDesktop)
    const cachedUrl = forceReload ? null : readCachedSportsUrl(cacheKey)
    if (cachedUrl) {
      setApiError(null)
      showUrl(cachedUrl)
      return
    }

    const playGame = async () => {
      // Cleared per attempt, or a later success still shows the error page.
      setApiError(null)
      setIsLoading(true)

      // Not via the fallback wrapper — see docs/memory/sports-entry-funsports-lobby.md
      try {
        const { status, url, error } = await fetchSportsLauncherUrl({
          isDesktop,
          username,
        })
        if (cancelled) return

        if (status === 1 && url) {
          showUrl(url)
        } else {
          setIsLoading(false)
          setApiError(error ?? 'No se pudo cargar el juego. Inténtalo de nuevo.')
        }
      } catch {
        if (cancelled) return
        setIsLoading(false)
        setApiError('No se pudo cargar el juego. Inténtalo de nuevo.')
      }
    }
    playGame()

    // Identity can change mid-flight; a stale response must not land last.
    return () => {
      cancelled = true
    }
  }, [isDesktop, isUserLoading, username, reloadNonce])

  // Fixed to the viewport: the iframe must keep its exact DOM position.
  const bare = isFullScreen
  const style: CSSProperties = {
    position: 'fixed',
    left: 0,
    right: 0,
    top: SPORTS_SHOW_HEADER && !bare ? CHROME_BAR_HEIGHT : 0,
    bottom: SPORTS_SHOW_BOTTOM_NAV && !isDesktop && !bare ? CHROME_BAR_HEIGHT : 0,
    // Below the bottom nav (120) and modals (203+); behind everything when parked.
    zIndex: active ? 10 : -1,
    visibility: active ? 'visible' : 'hidden',
    pointerEvents: active ? 'auto' : 'none',
  }

  return (
    <div style={style} id="sports-lobby">
      {isLoading && !apiError && (
        <GameLoading variant="sports" fullScreen={!SPORTS_SHOW_HEADER || bare} />
      )}
      {apiError ? (
        <ErrorPage errorMessage={apiError} />
      ) : (
        gameUrl && (
          <iframe
            className="w-full h-full border-0 outline-0"
            src={eventId ? `${gameUrl}&eventId=${eventId}` : gameUrl}
            onLoad={() => setIsLoading(false)}
          />
        )
      )}
    </div>
  )
}

// Owns the lobby's lifetime: a route unmount would drop the connection.
const SportsLobbyHost = () => {
  const pathname = useRouterState({ select: s => s.location.pathname })
  const eventId = useRouterState({
    select: s => new URLSearchParams(s.location.searchStr).get('eventId') ?? '',
  })
  const active = normalizePathname(pathname) === SPORTS_PATH
  const [mounted, setMounted] = useState(active)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  // Read in the timer callback, so leaving before the first visit arms nothing.
  const mountedRef = useRef(mounted)
  mountedRef.current = mounted

  useEffect(() => {
    if (active) {
      clearTimeout(timer.current)
      timer.current = undefined
      setMounted(true)
      return
    }
    if (!mountedRef.current) return
    timer.current = setTimeout(() => setMounted(false), SPORTS_KEEP_ALIVE_MS)
    return () => clearTimeout(timer.current)
  }, [active])

  if (!mounted) return null

  // A different eventId is an explicit request for another match.
  return <SportsLobby active={active} eventId={eventId} />
}

export default SportsLobbyHost
